var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { ConsentUIFlow, primaryConsentTypes } from "healthex-common/consent.dtos";
import { isAlphaNumeric } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
import { HXDesktopStudiesSearchType } from "./HXDesktopStudiesSearchViewModel";
import { NamedRoutes } from "./NamedRoutes";
import { PatientConsentFlow } from "./PatientConsentUIManager";
export class PatientsSearchViewModel {
    constructor(container) {
        this.lastestSearchDate = new Date();
        this.disponser = new Disposer();
        this.searchText = null;
        this.patientDetails = NOT_LOADED;
        this.isValidMRN = true;
        this.routingUIManager = container.routingUIManager;
        this.authManager = container.authManager;
        this.mrnSearchService = container.mrnSearchService;
        this.patientConsentUIManager = container.patientConsentUIManager;
    }
    run() {
        this.disponser.reaction(() => this.routingMRN, async () => {
            var _a;
            this.searchText = (_a = this.routingMRN) !== null && _a !== void 0 ? _a : null;
            await this.searchForPatientAPI();
        }, { fireImmediately: true });
    }
    stop() {
        this.disponser.dispose();
    }
    get routingMRN() {
        var _a;
        return (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.mrn;
    }
    get searchErrorText() {
        return this.isValidMRN ? null : "MRNs can only contain alphabet characters (a-z, A-Z) or numeric characters (0-9)";
    }
    get isEmbeddedProvider() {
        return this.authManager.isEmbeddedProvider;
    }
    updateSearchText(searchText) {
        this.searchText = searchText;
    }
    async searchForPatientAPI() {
        if (!this.searchText || !this.authManager.orgId)
            return;
        this.isValidMRN = isAlphaNumeric(this.searchText);
        if (!this.isValidMRN)
            return;
        const searchDate = new Date();
        this.lastestSearchDate = searchDate;
        this.patientDetails = NOT_LOADED;
        const patientDetails = await this.mrnSearchService.searchByMRNAndGetPatientDetails(this.authManager.orgId, {
            mrn: this.searchText,
            consentRequestSearchInput: {
                consentType: primaryConsentTypes,
            },
            start: 0,
            end: 10,
        });
        if (searchDate === this.lastestSearchDate) {
            this.patientDetails = patientDetails;
        }
    }
    async searchForPatient() {
        const mrn = this.searchText;
        if (!mrn)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_SEARCH, { mrn });
    }
    findConsents(pendingConsentsByStudy) {
        const consents = pendingConsentsByStudy.consents;
        const enrollmentConsentProgress = consents.find((consent) => primaryConsentTypes.includes(consent.consentType));
        return { enrollmentConsentProgress };
    }
    findConsentFlowProgress(pendingConsentsByStudy) {
        const consentFlowProgresses = pendingConsentsByStudy.study.consentFlowProgress;
        const enrollmentConsentFlowProgress = consentFlowProgresses === null || consentFlowProgresses === void 0 ? void 0 : consentFlowProgresses.find((consentFlowProgress) => consentFlowProgress.consentUIFlow === ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE);
        return { enrollmentConsentFlowProgress };
    }
    getGuidedConsentChipText(pendingConsentsByStudy) {
        const { enrollmentConsentProgress } = this.findConsents(pendingConsentsByStudy);
        if (enrollmentConsentProgress) {
            return "Enrollment request";
        }
        return "Request";
    }
    getGuidedConsentRequestedTimestamp(pendingConsentsByStudy) {
        const { enrollmentConsentProgress } = this.findConsents(pendingConsentsByStudy);
        if (enrollmentConsentProgress) {
            return enrollmentConsentProgress.timestamp;
        }
        return null;
    }
    getGuidedConsentButtonText(pendingConsentsByStudy) {
        const { enrollmentConsentFlowProgress } = this.findConsentFlowProgress(pendingConsentsByStudy);
        if (enrollmentConsentFlowProgress) {
            return enrollmentConsentFlowProgress.continueGuidedConsent ? "Continue guided consent" : "Start guided consent";
        }
        return "Start guided consent";
    }
    async goToGuidedConsent(pendingConsentsByStudy) {
        const { enrollmentConsentProgress } = this.findConsents(pendingConsentsByStudy);
        let consentFlow;
        if (enrollmentConsentProgress) {
            consentFlow = PatientConsentFlow.ENROLLMENT;
        }
        else {
            console.warn("No consent found");
            return;
        }
        const mrn = this.searchText;
        this.patientConsentUIManager.startConsentFlow(pendingConsentsByStudy.study.id, consentFlow, mrn, null);
    }
    goToStudyDetails(pendingConsentsByStudy) {
        const mrn = this.searchText;
        if (!mrn)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_STUDY_DETAILS, { mrn, studyId: pendingConsentsByStudy.study.id });
    }
    onStudiesSearchRowClick(info, mrn) {
        if (!mrn)
            return;
        const { studyId, searchType } = info;
        if (searchType === HXDesktopStudiesSearchType.OTHER_PROJECTS) {
            this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_STUDY_DETAILS, { mrn, studyId });
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientsSearchViewModel.prototype, "searchText", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientsSearchViewModel.prototype, "patientDetails", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientsSearchViewModel.prototype, "isValidMRN", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientsSearchViewModel.prototype, "routingMRN", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientsSearchViewModel.prototype, "searchErrorText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientsSearchViewModel.prototype, "isEmbeddedProvider", null);
